/* The Aesthetic Cove — motion layer.
   Smooth scroll (Lenis, mouse only), scroll reveals and magnetic buttons.
   Reveals run on IntersectionObserver and CSS transitions (base.css), so no
   scroll library is loaded on any page; GSAP is only fetched on the homepage
   for the intro. Everything here is optional: if the visitor prefers reduced
   motion, or the theme editor is open, the page is shown fully and still.
   (The snowflakes from the pointer live in snowfall.js.) */

(() => {
  const root = document.documentElement;
  const theme = window.theme || {};
  const motion = theme.motion || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const showAll = () => root.classList.add('motion-off');

  if (reduce || theme.designMode || !('IntersectionObserver' in window)) {
    showAll();
    window.dispatchEvent(new Event('motion:ready'));
    return;
  }

  /* ---------- smooth scroll ----------
     Mouse wheels move in steps of about 100px; Lenis eases each step with a
     light lerp so the page glides without lagging behind the hand. Phones and
     trackpads already scroll smoothly and are left alone: on a touch screen
     Lenis is not started at all, because even with syncTouch off it listens
     to every touch in a way that makes the phone wait on the page's scripts
     before it can move, which is what made swiping feel sticky.
     Anything that scrolls on its own (drawers, the shop panel, search,
     sideways rows) keeps its native wheel. */
  if (motion.smooth && window.Lenis && fine) {
    const lenis = new window.Lenis({
      lerp: 0.11,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false,
      prevent: (node) => !!(node.closest && node.closest('[data-lenis-prevent], .drawer, .search-modal, .mega__panel, .cart-drawer__body')),
    });
    window.lenis = lenis;
    const tick = (time) => {
      lenis.raf(time);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href*="#"]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const url = new URL(a.href, window.location.href);
      if (url.pathname !== window.location.pathname || url.hash.length < 2) return;
      let target = null;
      try {
        target = document.querySelector(url.hash);
      } catch (err) {
        return;
      }
      if (!target) return;
      e.preventDefault();
      const header = document.querySelector('.header');
      lenis.scrollTo(target, { offset: -((header && header.offsetHeight) || 76) + 1, lerp: 0.09 });
    });
  }

  /* ---------- reveals ----------
     Each [data-reveal] starts hidden (base.css) and gets data-revealed when it
     comes within 10% of the bottom of the screen; the CSS transition does the
     rest. Elements already on screen in the same pass stagger by 60ms, plus
     any data-reveal-delay the section asked for. */
  const DONE_AFTER = 1300;
  const show = (el, index) => {
    if (el.dataset.revealed) return;
    const delay = Number(el.dataset.revealDelay || 0) + (index || 0) * 0.06;
    if (delay) el.style.transitionDelay = `${delay}s`;
    el.dataset.revealed = '1';
    setTimeout(() => {
      el.dataset.revealed = 'done';
      el.style.transitionDelay = '';
    }, DONE_AFTER + delay * 1000);
  };

  let io = null;
  const observe = (scope = document) => {
    if (!motion.reveals) {
      showAll();
      return;
    }
    if (!io) {
      io = new IntersectionObserver(
        (entries) => {
          let i = 0;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            show(entry.target, i++);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
      );
    }
    scope.querySelectorAll('[data-reveal]:not([data-revealed])').forEach((el) => io.observe(el));
  };

  /* ---------- magnetic buttons (mouse only) ---------- */
  const magnetise = (scope = document) => {
    if (!fine) return;
    scope.querySelectorAll('[data-magnetic]').forEach((el) => {
      if (el.dataset.magnet) return;
      el.dataset.magnet = '1';
      let tx = 0;
      let ty = 0;
      let cx = 0;
      let cy = 0;
      let raf = 0;
      const step = () => {
        cx += (tx - cx) * 0.18;
        cy += (ty - cy) * 0.18;
        el.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          raf = requestAnimationFrame(step);
        } else {
          raf = 0;
          if (!tx && !ty) el.style.transform = '';
        }
      };
      const go = () => {
        if (!raf) raf = requestAnimationFrame(step);
      };
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        tx = (e.clientX - r.left - r.width / 2) * 0.28;
        ty = (e.clientY - r.top - r.height / 2) * 0.35;
        go();
      });
      el.addEventListener('pointerleave', () => {
        tx = 0;
        ty = 0;
        go();
      });
    });
  };

  /* ---------- boot ---------- */
  const boot = () => {
    observe();
    magnetise();
    window.dispatchEvent(new Event('motion:ready'));
  };

  // The intro (if present) calls window.themeMotionStart when it lifts,
  // so reveals at the top of the page play after the curtain.
  if (document.querySelector('[data-intro]:not([data-skip])')) {
    window.themeMotionStart = boot;
  } else {
    boot();
  }

  document.addEventListener('theme:content', (e) => {
    observe(e.detail || document);
    magnetise(e.detail || document);
  });

  // Failsafe: never leave content invisible.
  setTimeout(() => {
    document.querySelectorAll('[data-reveal]:not([data-revealed])').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) show(el, 0);
    });
  }, 6000);
})();
